import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Calendar,
    ChevronDown,
    ChevronUp,
    Clock,
    Scale,
    Timer,
    User,
} from "lucide-react";

const DURUM_STYLES = {
    BEKLIYOR: "text-slate-300 border-slate-500/35 bg-slate-500/10",
    YAKLASIYOR: "text-amber-200 border-amber-500/40 bg-amber-500/10",
    HAZIR: "text-emerald-200 border-emerald-500/40 bg-emerald-500/10",
    GECIKTI: "text-rose-200 border-rose-500/40 bg-rose-500/10",
};

const TAB_OPTIONS = [
    { key: "kritik", label: "Kritik kuyruk", hint: "Gecikmiş + hazır + yaklaşan" },
    { key: "GECIKTI", label: "Gecikmiş", accent: "rose" },
    { key: "HAZIR", label: "Hazır (bugün)", accent: "emerald" },
    { key: "YAKLASIYOR", label: "Yaklaşıyor", accent: "amber" },
    { key: "BEKLIYOR", label: "Bekliyor", accent: "slate" },
    { key: "tum", label: "Tüm bekleyenler" },
];

function formatTrDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("tr-TR");
    } catch {
        return iso;
    }
}

function ProgressBar({ pct, durum }) {
    const v = pct == null ? 0 : Math.min(100, Math.max(0, pct));
    const bar =
        durum === "GECIKTI"
            ? "bg-rose-500"
            : durum === "HAZIR"
              ? "bg-emerald-500"
              : durum === "YAKLASIYOR"
                ? "bg-amber-500"
                : "bg-sky-500";
    return (
        <div className="flex items-center gap-2 min-w-[88px]">
            <div className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
                <div className={`h-full rounded-full ${bar}`} style={{ width: `${v}%` }} />
            </div>
            <span className="text-[10px] tabular-nums text-slate-400 w-8 text-right">{v}%</span>
        </div>
    );
}

function GunCell({ value, tone = "neutral" }) {
    const cls =
        tone === "danger"
            ? "text-rose-300 font-semibold"
            : tone === "warn"
              ? "text-amber-300 font-semibold"
              : tone === "ok"
                ? "text-emerald-300 font-semibold"
                : "text-slate-300";
    return (
        <span className={`tabular-nums text-xs ${cls}`}>
            {value == null ? "—" : `${value} gün`}
        </span>
    );
}

function TahkimTable({ rows, onFilterChange, compact }) {
    if (!rows?.length) {
        return (
            <div className="text-center text-xs text-slate-500 py-6 border border-dashed border-white/10 rounded-lg">
                Bu grupta kayıt yok.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto overflow-y-auto max-h-[min(320px,42vh)] rounded-lg border border-white/10" style={{ scrollbarWidth: "thin" }}>
            <table className="w-full text-left text-[11px] min-w-[960px]">
                <thead>
                    <tr className="border-b border-white/10 bg-[#0B0F19]/80 text-[9px] uppercase tracking-wider text-slate-500">
                        <th className="px-2 py-2 font-medium">Dosya</th>
                        <th className="px-2 py-2 font-medium">Ad</th>
                        <th className="px-2 py-2 font-medium">Soyad</th>
                        <th className="px-2 py-2 font-medium">TC Kimlik</th>
                        <th className="px-2 py-2 font-medium">Plaka</th>
                        {!compact && <th className="px-2 py-2 font-medium">Sigorta</th>}
                        <th className="px-2 py-2 font-medium">Başvuru</th>
                        <th className="px-2 py-2 font-medium">Hedef</th>
                        <th className="px-2 py-2 font-medium text-center">Geçen</th>
                        <th className="px-2 py-2 font-medium text-center">Kalan</th>
                        <th className="px-2 py-2 font-medium text-center">Gecikme</th>
                        <th className="px-2 py-2 font-medium text-center">SLA</th>
                        {!compact && <th className="px-2 py-2 font-medium">İlerleme</th>}
                        <th className="px-2 py-2 font-medium">Durum</th>
                        {!compact && <th className="px-2 py-2 font-medium min-w-[180px]">Net özet</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const durum = (r.on_tahkim_sure_durumu || "BEKLIYOR").toUpperCase();
                        const rowBg =
                            durum === "GECIKTI"
                                ? "bg-rose-500/[0.06]"
                                : durum === "HAZIR"
                                  ? "bg-emerald-500/[0.06]"
                                  : durum === "YAKLASIYOR"
                                    ? "bg-amber-500/[0.05]"
                                    : "";
                        return (
                            <tr
                                key={r.id}
                                className={`border-b border-white/5 hover:bg-white/[0.03] ${rowBg}`}
                            >
                                <td className="px-2 py-2">
                                    <Link
                                        to={`/e/sigorta-dosyalari/${r.id}`}
                                        className="font-mono font-semibold text-[#F3E5AB] hover:underline"
                                    >
                                        {r.dosya_no}
                                    </Link>
                                </td>
                                <td className="px-2 py-2 text-slate-200">{r.muvekkil_ad || "—"}</td>
                                <td className="px-2 py-2 text-slate-200">{r.muvekkil_soyad || "—"}</td>
                                <td className="px-2 py-2 font-mono text-slate-400">{r.tc_kimlik || "—"}</td>
                                <td className="px-2 py-2 font-mono text-slate-500">{r.plaka || "—"}</td>
                                {!compact && (
                                    <td className="px-2 py-2 text-slate-400 truncate max-w-[100px]">
                                        {r.sigorta_sirketi || "—"}
                                    </td>
                                )}
                                <td className="px-2 py-2 text-slate-400 whitespace-nowrap">
                                    {formatTrDate(r.basvuru_gonderim_tarihi)}
                                </td>
                                <td className="px-2 py-2 text-slate-400 whitespace-nowrap">
                                    {formatTrDate(r.on_tahkim_hedef_tarihi)}
                                </td>
                                <td className="px-2 py-2 text-center">
                                    <GunCell value={r.gun_gecen} tone="neutral" />
                                </td>
                                <td className="px-2 py-2 text-center">
                                    <GunCell
                                        value={r.gun_gecikme > 0 ? null : r.gun_kaldi}
                                        tone={
                                            r.gun_kaldi != null && r.gun_kaldi <= 3 && r.gun_gecikme === 0
                                                ? "warn"
                                                : r.gun_kaldi === 0 && durum === "HAZIR"
                                                  ? "ok"
                                                  : "neutral"
                                        }
                                    />
                                </td>
                                <td className="px-2 py-2 text-center">
                                    <GunCell
                                        value={r.gun_gecikme > 0 ? r.gun_gecikme : null}
                                        tone={r.gun_gecikme > 0 ? "danger" : "neutral"}
                                    />
                                </td>
                                <td className="px-2 py-2 text-center">
                                    <span className="tabular-nums text-slate-400">
                                        {r.basvuru_bekleme_gun ?? "—"} gün
                                    </span>
                                </td>
                                {!compact && (
                                    <td className="px-2 py-2">
                                        <ProgressBar pct={r.ilerleme_yuzde} durum={durum} />
                                    </td>
                                )}
                                <td className="px-2 py-2">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onFilterChange?.({
                                                key: "on_tahkim_sure_durumu",
                                                value: durum,
                                            })
                                        }
                                        className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] whitespace-nowrap ${
                                            DURUM_STYLES[durum] || DURUM_STYLES.BEKLIYOR
                                        } hover:brightness-110`}
                                        title="Listeyi bu duruma filtrele"
                                    >
                                        {r.on_tahkim_sure_durumu_etiket || durum}
                                    </button>
                                </td>
                                {!compact && (
                                    <td className="px-2 py-2 text-[10px] text-slate-400 leading-snug">
                                        {r.net_gun_ozet || "—"}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default function TahkimSureDetayPanel({ detay, onFilterChange, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    const [tab, setTab] = useState("kritik");

    const ozet = detay?.ozet;
    const rows = useMemo(() => {
        if (!detay) return [];
        if (tab === "kritik") return detay.kritik_liste || [];
        if (tab === "tum") return detay.tum_liste || [];
        return detay.gruplar?.[tab] || [];
    }, [detay, tab]);

    if (!detay?.ozet?.toplam_bekleyen && !detay?.tum_liste?.length) {
        return null;
    }

    return (
        <div className="px-3 py-2 border-b border-white/5 bg-gradient-to-b from-[#0f1424]/80 to-transparent">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <Scale className="h-4 w-4 text-amber-400 shrink-0" />
                        <h3 className="text-sm font-semibold text-white">
                            Tahkime geçiş süre takibi — tam detay
                        </h3>
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 hover:text-slate-300"
                        >
                            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {open ? "Daralt" : "Genişlet"}
                        </button>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed max-w-3xl">
                        {detay.aciklama} · Referans tarih:{" "}
                        <span className="text-slate-300 font-medium">{detay.bugun_etiket}</span>
                    </p>
                </div>
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                    <span className="inline-flex items-center gap-1 rounded border border-white/10 bg-black/20 px-2 py-1 text-slate-400">
                        <User className="h-3 w-3" />
                        Bekleyen: <b className="text-white tabular-nums">{ozet?.toplam_bekleyen ?? 0}</b>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-rose-200">
                        <AlertTriangle className="h-3 w-3" />
                        Gecikmiş: <b className="tabular-nums">{ozet?.gecikti ?? 0}</b>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-200">
                        <Calendar className="h-3 w-3" />
                        Hazır: <b className="tabular-nums">{ozet?.hazir ?? 0}</b>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-amber-200">
                        <Timer className="h-3 w-3" />
                        Yaklaşıyor: <b className="tabular-nums">{ozet?.yaklasiyor ?? 0}</b>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded border border-slate-500/30 bg-slate-500/10 px-2 py-1 text-slate-300">
                        <Clock className="h-3 w-3" />
                        Bekliyor: <b className="tabular-nums">{ozet?.bekliyor ?? 0}</b>
                    </span>
                </div>
            </div>

            {open && (
                <>
                    <div className="flex flex-wrap gap-1 mb-2">
                        {TAB_OPTIONS.map((t) => {
                            const count =
                                t.key === "kritik"
                                    ? (detay.kritik_liste || []).length
                                    : t.key === "tum"
                                      ? (detay.tum_liste || []).length
                                      : (detay.gruplar?.[t.key] || []).length;
                            const active = tab === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setTab(t.key)}
                                    className={`px-2 py-1 rounded-md text-[10px] border transition ${
                                        active
                                            ? "border-[#D4AF37]/50 bg-[#D4AF37]/15 text-[#F3E5AB]"
                                            : "border-white/10 text-slate-500 hover:text-slate-300 hover:bg-white/5"
                                    }`}
                                    title={t.hint}
                                >
                                    {t.label}
                                    <span className="ml-1 tabular-nums opacity-80">({count})</span>
                                </button>
                            );
                        })}
                    </div>

                    <TahkimTable rows={rows} onFilterChange={onFilterChange} compact={false} />

                    <div className="mt-2 text-[9px] text-slate-600 leading-relaxed">
                        Net gün hesabı: <strong className="text-slate-500">Geçen</strong> = başvuru gönderiminden
                        bugüne · <strong className="text-slate-500">Kalan</strong> = hedef tarihe ·{" "}
                        <strong className="text-slate-500">Gecikme</strong> = hedef geçtiyse fazla gün · SLA
                        sigorta/kural bazlı (varsayılan 15, Sompo 21).
                    </div>
                </>
            )}
        </div>
    );
}
